import { useEffect, useRef } from 'react';
import { theme } from '@kvdbil/components';
import { useFloatingButtonVisibility } from '~/App/shared/hooks/useFloatingButtonVisibility';
import { useIsDevice } from '~/App/shared/hooks/useIsDevice';
import { useKundoChatToggle } from '~/App/shared/hooks/useKundoChatToggle';
import { useGA4User } from '~/App/shared/hooks/useGA4User';
import { trackGA4ChatInitiated } from '~/helpers/client/ga4TrackEvent';

type Styles = Partial<Record<keyof CSSStyleDeclaration, string | number>>;

const applyStyles = (el: Element | null | undefined, styles: Styles) => {
  if (el instanceof HTMLElement || el instanceof SVGElement) {
    Object.assign(el.style, styles);
  }
};

const KundoAiChatButton = () => {
  const { isMobile } = useIsDevice();
  const { host, button, isChatOpen } = useKundoChatToggle();
  const ga4User = useGA4User();
  const prevIsChatOpenRef = useRef<boolean | null>(null);
  const { isHidden, bottom, buttonPosition } = useFloatingButtonVisibility();

  // Track "chat initiated" on each `false → true` transition of `isChatOpen`.
  // The first value observed after the widget initialises is recorded without
  // firing, so a refresh mid-conversation (where Kundo restores the chat as
  // already open from localStorage) is not counted as a fresh initiation.
  useEffect(() => {
    if (!host) {
      return;
    }
    if (prevIsChatOpenRef.current === null) {
      prevIsChatOpenRef.current = isChatOpen;
      return;
    }
    if (!prevIsChatOpenRef.current && isChatOpen) {
      trackGA4ChatInitiated(ga4User);
    }
    prevIsChatOpenRef.current = isChatOpen;
  }, [host, isChatOpen, ga4User]);

  useEffect(() => {
    if (!host || !button) {
      return;
    }

    const visible = !isHidden;

    const background = button.children[0];
    const iconAndText = button.children[1];
    const icon = iconAndText?.children[0]?.children[0];
    const text = iconAndText?.children[1];

    applyStyles(button, {
      right: '0.75rem',
      bottom,
      color: theme.colors.text.dark,
      boxShadow: theme.elevations.elevation16,
      height: '3.75rem',
      width: isMobile ? '3.75rem' : '',
      padding: isMobile ? '0' : '',
      visibility: visible ? 'visible' : 'hidden',
      opacity: visible ? '1' : '0',
      transition: 'opacity 100ms ease-in-out',
      ...buttonPosition
    });

    applyStyles(background, {
      background: theme.colors.primary.main
    });

    applyStyles(iconAndText, {
      padding: isMobile ? '0' : ''
    });

    applyStyles(icon, {
      width: '28px',
      height: '28px'
    });

    applyStyles(text, {
      color: theme.colors.text.dark,
      fontFamily: theme.typography.fontAlternativeFamily,
      fontWeight: theme.typography.fontAlternativeBoldWeight,
      fontSize: '20px',
      display: isMobile ? 'none' : ''
    });
  }, [host, button, isHidden, bottom, buttonPosition, isMobile]);

  return null;
};

export default KundoAiChatButton;
